import React, { useEffect, useState } from "react";
import { BusinessEmailInput } from "@/components/ui/business-email-input";
import { dateLocale } from "@/lib/dateTime";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { unwrapPage, usePagedList } from "@/lib/paging";
import { ListPager } from "@/components/ui/list-pager";
import { SearchSelect } from "@/components/ui/search-select";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/context/ConfirmContext";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import { cn } from "@/lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ErrorState, PageHeader, StatusPill, ViewsPanel, initialsOf, type Tone } from "@/components/patterns";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  UserPlus,
  MoreHorizontal,
  Edit,
  Shield,
  KeyRound,
  Power,
  Trash2,
  Loader2,
  Mail,
  Send,
  Unlock,
  Copy,
  ShieldOff,
} from "lucide-react";
import type { ApiResponse, RoleDetail } from "@/types";

interface UserRecord {
  id: string | number;
  username: string;
  first_name?: string;
  last_name?: string;
  firstName?: string;
  lastName?: string;
  email: string;
  mobile?: string;
  status: boolean;
  role: string;
  user_role_id?: string | number;
  user_role?: any;
  manager_id?: string;
  manager?: {
    id: string;
    first_name?: string;
    last_name?: string;
    email?: string;
  };
  created_on?: string;
  /** ACTIVE: has chosen a password · INVITED: invite link still valid · INVITE_EXPIRED: needs a new invite */
  invite_status?: "ACTIVE" | "INVITED" | "INVITE_EXPIRED";
  invite_expires_at?: string | null;
  /** Sign-in temporarily locked after too many wrong passwords */
  locked?: boolean;
  last_login_at?: string | null;
  /** Has two-step verification (authenticator app) on */
  two_factor_enabled?: boolean;
}

type UserStats = {
  counts: Record<string, number>;
  teams: { id: string; head: string; head_active: boolean; members: number; active_members: number }[];
};

/** "just now", "3 h ago", "yesterday", "12 Oct" */
function lastSeen(iso?: string | null) {
  if (!iso) return "Never";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 2) return "Just now";
  if (mins < 60) return `${mins} min ago`;
  if (mins < 60 * 24) return `${Math.floor(mins / 60)} h ago`;
  if (mins < 60 * 48) return "Yesterday";
  return new Date(iso).toLocaleDateString(dateLocale(), { day: "numeric", month: "short" });
}

/** One label and colour per account state; locked beats the rest because it needs action now */
function accountState(u: UserRecord): { label: string; tone: Tone } {
  if (u.status === false) return { label: "Deactivated", tone: "neutral" };
  if (u.locked) return { label: "Locked", tone: "danger" };
  if (u.invite_status === "INVITED") return { label: "Invited", tone: "warning" };
  if (u.invite_status === "INVITE_EXPIRED") return { label: "Invite expired", tone: "danger" };
  return { label: "Active", tone: "success" };
}

/** What each built-in role is for, shown on the invite */
const ROLE_BLURB: Record<string, string> = {
  SALES_HEAD: "Leads a team, approves discounts, sets targets.",
  SALES_EXEC: "Works their own leads and deals day to day.",
};

/** A one-time link to hand to a user (shown once; the server keeps only a scrambled copy) */
type SharedLink = { kind: "invite" | "reset"; email: string; url: string; expires_at: string; emailed: boolean };

export function UsersPage() {
  const [roles, setRoles] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [addManagerLabel, setAddManagerLabel] = useState("");
  const [roleManagerLabel, setRoleManagerLabel] = useState("");
  const [selectedRole, setSelectedRole] = useState("all");
  const [view, setView] = useState("all");
  const [stats, setStats] = useState<UserStats | null>(null);

  // Dialog States
  const [addOpen, setAddOpen] = useState(false);
  const [editUser, setEditUser] = useState<UserRecord | null>(null);
  const [changeRoleUser, setChangeRoleUser] = useState<UserRecord | null>(null);
  const [sharedLink, setSharedLink] = useState<SharedLink | null>(null);
  const [deleteUser, setDeleteUser] = useState<UserRecord | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // The company email domain, when the server has one: people then type only the part before the @
  const [businessDomain, setBusinessDomain] = useState<string | null>(null);
  useEffect(() => {
    api
      .get("/api/User/EmailPolicy")
      .then((res) => setBusinessDomain(unwrapObject<{ business_domain: string | null }>(res).business_domain || null))
      .catch(() => setBusinessDomain(null));
  }, []);

  // Form states
  const [addForm, setAddForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    user_name: "",
    mobile: "",
    user_role_id: "",
    manager_id: "",
    send_welcome_email: true,
  });

  const [editForm, setEditForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    user_name: "",
    mobile: "",
  });

  const [newRoleId, setNewRoleId] = useState("");
  const [newManagerId, setNewManagerId] = useState("none");

  const { success, error } = useToast();
  const { user: me } = useAuth();
  const confirm = useConfirm();
  const isSuperAdmin = String(me?.UserRole?.RoleCode || "").toUpperCase() === "SUPER_ADMIN";

  useEffect(() => {
    api
      .get("/api/UserRole/List")
      .then((res) => {
        const rawRoles = unwrapArray<RoleDetail>(res);
        setRoles(
          rawRoles.filter(
            (r) => (r.code || "").toUpperCase() !== "SUPER_ADMIN" && (r.name || "").toLowerCase() !== "super admin",
          ),
        );
      })
      .catch((err: any) => error("Fetch Error", err.message || "Failed to load roles"));
  }, []);

  const list = usePagedList<UserRecord>({
    deps: [search, selectedRole, view],
    debounceMs: search.trim() ? 350 : 0,
    load: async (page) => {
      const qs = new URLSearchParams({ page: String(page), page_size: "30" });
      if (search.trim()) qs.set("search", search.trim());
      if (selectedRole !== "all") qs.set("role_id", selectedRole);
      if (view !== "all") qs.set("status", view);
      try {
        return unwrapPage<UserRecord>(await api.get(`/api/User/List?${qs}`));
      } catch (err: any) {
        throw err;
      }
    },
  });
  const filteredUsers = list.rows;
  const loading = list.loading;
  const loadStats = () =>
    api
      .get("/api/User/Stats")
      .then((res: any) => setStats((res?.data || res) as UserStats))
      .catch(() => {});
  useEffect(() => {
    loadStats();
  }, []);
  // Every change below reloads the list and the counts together
  const loadData = async () => {
    await Promise.all([list.reload(), loadStats()]);
  };

  const isSalesExecRole = (roleId: string) =>
    (roles.find((r: any) => String(r.id || r.Id) === String(roleId))?.code || "").toUpperCase() === "SALES_EXEC";

  const salesHeadRoleId = String(roles.find((r: any) => (r.code || "").toUpperCase() === "SALES_HEAD")?.id || "");

  const managerName = (row: any) => {
    const name = `${row.first_name || row.firstName || ""} ${row.last_name || row.lastName || ""}`.trim() || row.username;
    const roleName = row.user_role?.name || row.role || "User";
    return `${name} (${roleName})`;
  };

  // Handle Add User: the user gets an invite link to choose their own password
  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addForm.email || !addForm.first_name || !addForm.user_role_id) {
      error("Validation Error", "Please fill all required fields");
      return;
    }
    if (isSalesExecRole(addForm.user_role_id) && !addForm.manager_id) {
      error("Validation Error", "A Sales Executive must report to a Sales Head");
      return;
    }

    setActionLoading(true);
    try {
      const res: any = await api.post("/api/User/Insert", {
        first_name: addForm.first_name,
        last_name: addForm.last_name || undefined,
        email: addForm.email,
        username: addForm.user_name || addForm.email.split("@")[0],
        mobile: addForm.mobile || undefined,
        user_role_id: addForm.user_role_id,
        manager_id: addForm.manager_id || undefined,
        send_welcome_email: addForm.send_welcome_email,
      });
      const created = res?.AddtionalData || {};
      success("User Created", created.emailed ? "Invite emailed" : "Share the invite link with the user");
      if (created.invite_url) {
        setSharedLink({
          kind: "invite",
          email: addForm.email,
          url: created.invite_url,
          expires_at: created.invite_expires_at,
          emailed: !!created.emailed,
        });
      }
      setAddOpen(false);
      setAddForm({
        first_name: "",
        last_name: "",
        email: "",
        user_name: "",
        mobile: "",
        user_role_id: "",
        manager_id: "",
        send_welcome_email: true,
      });
      await loadData();
    } catch (err: any) {
      error("Creation Failed", err.message || "Could not create user");
    } finally {
      setActionLoading(false);
    }
  };

  // Handle Edit User
  const handleEditOpen = (u: UserRecord) => {
    setEditUser(u);
    setEditForm({
      first_name: u.first_name || u.firstName || "",
      last_name: u.last_name || u.lastName || "",
      email: u.email,
      user_name: u.username,
      mobile: u.mobile || "",
    });
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editUser) return;

    setActionLoading(true);
    try {
      const { user_name, ...rest } = editForm;
      await api.put(`/api/User/Update/${editUser.id}`, { ...rest, username: user_name });
      success("User Updated", "User details saved successfully");
      setEditUser(null);
      await loadData();
    } catch (err: any) {
      error("Update Failed", err.message || "Failed to update user");
    } finally {
      setActionLoading(false);
    }
  };

  // Toggle Suspend / Activate
  const handleToggleStatus = async (u: UserRecord) => {
    try {
      const res = await api.patch(`/api/User/SuspendOrActivate/${u.id}`);
      success("Status Updated", res.message || "User status updated");
      await loadData();
    } catch (err: any) {
      error("Action Failed", err.message || "Failed to toggle status");
    }
  };

  // Handle Update Role & Manager
  const handleRoleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!changeRoleUser || !newRoleId) return;
    if (isSalesExecRole(newRoleId) && newManagerId === "none") {
      error("Validation Error", "A Sales Executive must report to a Sales Head");
      return;
    }

    setActionLoading(true);
    try {
      await api.patch(`/api/User/UpdateRole/${changeRoleUser.id}`, {
        user_role_id: newRoleId,
        manager_id: newManagerId === "none" ? null : newManagerId,
      });
      success("Role & Reporting Updated", "User security role and reporting manager updated successfully");
      setChangeRoleUser(null);
      setNewRoleId("");
      setNewManagerId("none");
      await loadData();
    } catch (err: any) {
      error("Role Update Failed", err.message || "Failed to update role");
    } finally {
      setActionLoading(false);
    }
  };

  // Invite / reset links: the server emails them and returns them once, so the admin can also share them
  const handleSendLink = async (u: UserRecord, kind: "invite" | "reset") => {
    setActionLoading(true);
    try {
      const res: any =
        kind === "invite"
          ? await api.post(`/api/User/ResendInvite/${u.id}`)
          : await api.post(`/api/User/SendPasswordReset/${u.id}`);
      const data = res?.AddtionalData || {};
      setSharedLink({
        kind,
        email: u.email,
        url: data.invite_url || data.reset_url,
        expires_at: data.invite_expires_at || data.reset_expires_at,
        emailed: !!data.emailed,
      });
      await loadData();
    } catch (err: any) {
      error("Could not create link", err.message || "Please try again");
    } finally {
      setActionLoading(false);
    }
  };

  /** For a lost or replaced phone: turns two-step verification off and signs the person out everywhere */
  const handleResetTwoFactor = async (u: UserRecord) => {
    const ok = await confirm({
      title: `Reset two-step verification for ${personName(u)}?`,
      message: "It is turned off, they are signed out everywhere, and they are told by email. They can set it up again on their new phone from Settings, Preferences, Security.",
      confirmLabel: "Reset it",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.post(`/api/User/ResetTwoFactor/${u.id}`, {});
      success("Two-step verification reset", `${u.email} was signed out and can set it up again`);
      await loadData();
    } catch (err: any) {
      error("Reset failed", err.message || "Please try again");
    }
  };

  const handleUnlock = async (u: UserRecord) => {
    try {
      await api.patch(`/api/User/Unlock/${u.id}`);
      success("Sign-in unlocked", `${u.email} can sign in again`);
      await loadData();
    } catch (err: any) {
      error("Unlock failed", err.message || "Please try again");
    }
  };

  const copyLink = async () => {
    if (!sharedLink) return;
    try {
      await navigator.clipboard.writeText(sharedLink.url);
      success("Link copied", "Send it to the user privately");
    } catch {
      error("Copy failed", "Select the link and copy it manually");
    }
  };

  // Handle Delete
  const handleDelete = async () => {
    if (!deleteUser) return;
    setActionLoading(true);
    try {
      await api.delete(`/api/User/Delete/${deleteUser.id}`);
      success("User Removed", "User account deleted successfully");
      setDeleteUser(null);
      await loadData();
    } catch (err: any) {
      error("Delete Failed", err.message || "Failed to delete user");
    } finally {
      setActionLoading(false);
    }
  };

  const c = stats?.counts || {};
  const roleName = (u: UserRecord) => u.user_role?.name || u.user_role?.RoleName || u.role || "User";
  const personName = (u: UserRecord) => `${u.first_name || u.firstName || ""} ${u.last_name || u.lastName || ""}`.trim() || u.username;

  /** The one action that fits where this person is stuck; everything else stays in the menu */
  const primaryAction = (u: UserRecord) => {
    if (u.status === false) return { label: "Reactivate", run: () => handleToggleStatus(u) };
    if (u.locked) return { label: "Unlock", run: () => handleUnlock(u) };
    if (u.invite_status && u.invite_status !== "ACTIVE") return { label: "Resend invite", run: () => handleSendLink(u, "invite") };
    return { label: "Edit", run: () => handleEditOpen(u) };
  };

  return (
    <div className="space-y-4">
      <ViewsPanel
        title="Users & teams"
        subtitle="Who can sign in"
        active={view}
        onSelect={setView}
        groups={[
          {
            items: [
              { key: "all", label: "Everyone", count: c.all },
              { key: "active", label: "Active", count: c.active },
              { key: "invited", label: "Invited, not joined", count: c.invited, tone: "warning" },
              { key: "locked", label: "Locked out", count: c.locked, tone: "danger" },
              { key: "deactivated", label: "Deactivated", count: c.deactivated },
            ],
          },
        ]}
      />

      <PageHeader
        title="Users & teams"
        summary={list.total}
        actions={
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <UserPlus className="size-4 mr-1.5" />
            Invite user
          </Button>
        }
      />

      {stats && stats.teams.length > 0 && (
        <div className="flex gap-3 overflow-x-auto pb-1">
          {stats.teams.map((t) => (
            <div key={t.id} className="min-w-[200px] rounded-xl border border-border bg-card px-4 py-3 shadow-xs">
              <div className="flex items-center gap-2">
                <span className="flex size-7 items-center justify-center rounded-full bg-accent text-[10px] font-bold text-accent-foreground">{initialsOf(t.head)}</span>
                <div className="min-w-0">
                  <p className="truncate text-[13px] font-semibold text-foreground">{t.head}’s team</p>
                  <p className="text-[11.5px] text-muted-foreground">{t.head_active ? "Sales Head" : "Sales Head · deactivated"}</p>
                </div>
              </div>
              <p className="mt-2 text-[12.5px] text-muted-foreground">
                <span className="font-mono font-semibold text-foreground">{t.members}</span> {t.members === 1 ? "person" : "people"}
                {t.members > t.active_members ? <> · {t.members - t.active_members} off</> : null}
              </p>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-col sm:flex-row items-center gap-3">
        <Input placeholder="Search name, email or username" value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 flex-1 text-sm" />
        <div className="w-full sm:w-56">
          <Select value={selectedRole} onValueChange={setSelectedRole}>
            <SelectTrigger className="h-9 text-sm">
              <SelectValue placeholder="Role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              {roles.map((r: any) => (
                <SelectItem key={r.id || r.Id} value={String(r.id || r.Id)}>
                  {r.name || r.RoleName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {list.error ? (
        <ErrorState title="Couldn't load users" onRetry={list.reload} error={list.error} />
      ) : loading && filteredUsers.length === 0 ? (
        <TableSkeleton rows={5} columns={5} />
      ) : (
        <div className="rounded-lg border border-border bg-card shadow-xs overflow-hidden">
          <Table className="min-w-[960px]">
            <TableHeader>
              <TableRow>
                <TableHead>Person</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Team · reports to</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last sign-in</TableHead>
                <TableHead className="text-right"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map((u) => {
                const fullName = personName(u);
                const state = accountState(u);
                const act = primaryAction(u);
                return (
                  <TableRow key={u.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-accent text-[11px] font-bold text-accent-foreground">{initialsOf(fullName)}</span>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold leading-tight text-foreground">{fullName}</p>
                          <p className="truncate text-xs text-muted-foreground">{u.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{roleName(u)}</TableCell>
                    <TableCell className="text-sm">
                      {u.manager ? `${u.manager.first_name || ""} ${u.manager.last_name || ""}`.trim() : <span className="text-muted-foreground">Reports to no one</span>}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <StatusPill tone={state.tone}>{state.label}</StatusPill>
                        {u.two_factor_enabled && <StatusPill tone="info">Two-step</StatusPill>}
                      </div>
                    </TableCell>
                    <TableCell className={cn("text-sm", u.last_login_at ? "text-foreground" : "text-muted-foreground")}>{lastSeen(u.last_login_at)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button variant="outline" size="sm" className="h-7 px-2.5 text-xs" disabled={actionLoading} onClick={act.run}>
                          {act.label}
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon-sm" aria-label={`More actions for ${fullName}`}>
                              <MoreHorizontal className="size-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuItem onClick={() => handleEditOpen(u)} className="cursor-pointer">
                              <Edit className="size-4 mr-2" />
                              Edit Details
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => {
                                setChangeRoleUser(u);
                                setNewRoleId(String(u.user_role_id || u.user_role?.id || ""));
                                setNewManagerId(u.manager_id || "none");
                                setRoleManagerLabel(u.manager ? `${u.manager.first_name || ""} ${u.manager.last_name || ""}`.trim() : "");
                              }}
                              className="cursor-pointer"
                            >
                              <Shield className="size-4 mr-2" />
                              Change Role
                            </DropdownMenuItem>
                            {u.invite_status && u.invite_status !== "ACTIVE" ? (
                              <DropdownMenuItem onClick={() => handleSendLink(u, "invite")} disabled={u.status === false || actionLoading} className="cursor-pointer">
                                <Send className="size-4 mr-2" />
                                Resend Invite
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={() => handleSendLink(u, "reset")} disabled={u.status === false || actionLoading} className="cursor-pointer">
                                <KeyRound className="size-4 mr-2" />
                                Send Password Reset Link
                              </DropdownMenuItem>
                            )}
                            {u.locked && (
                              <DropdownMenuItem onClick={() => handleUnlock(u)} className="cursor-pointer">
                                <Unlock className="size-4 mr-2" />
                                Unlock Sign-in
                              </DropdownMenuItem>
                            )}
                            {isSuperAdmin && u.two_factor_enabled && (
                              <DropdownMenuItem onClick={() => handleResetTwoFactor(u)} className="cursor-pointer">
                                <ShieldOff className="size-4 mr-2" />
                                Reset Two-step Verification
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => handleToggleStatus(u)} className="cursor-pointer">
                              <Power className="size-4 mr-2" />
                              {u.status !== false ? "Deactivate Account" : "Reactivate Account"}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => setDeleteUser(u)} className="cursor-pointer text-danger focus:text-danger">
                              <Trash2 className="size-4 mr-2" />
                              Delete Account
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {filteredUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-muted-foreground text-sm">
                    No users match this view.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <ListPager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} disabled={loading} />
        </div>
      )}

      {/* Invite a person: a drawer, as in the design */}
      <Sheet open={addOpen} onOpenChange={setAddOpen}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-[420px]">
          <SheetHeader className="border-b border-border px-5 py-4">
            <SheetTitle className="text-base">Invite a person</SheetTitle>
            <SheetDescription className="sr-only">Send an invitation to a new user</SheetDescription>
          </SheetHeader>
          <form onSubmit={handleAddSubmit} className="flex min-h-0 flex-1 flex-col">
            <div className="flex-1 space-y-3.5 overflow-y-auto px-5 py-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>First name</Label>
                  <Input required value={addForm.first_name} onChange={(e) => setAddForm({ ...addForm, first_name: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Last name</Label>
                  <Input value={addForm.last_name} onChange={(e) => setAddForm({ ...addForm, last_name: e.target.value })} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Work email</Label>
                <BusinessEmailInput required domain={businessDomain} value={addForm.email} onChange={(email) => setAddForm({ ...addForm, email })} />
                <p className="text-[11.5px] text-muted-foreground">{businessDomain && addForm.email ? `The invite goes to ${addForm.email}.` : "The invite goes here."}</p>
              </div>
              <div className="space-y-1.5">
                <Label>Mobile (for WhatsApp alerts)</Label>
                <Input value={addForm.mobile} onChange={(e) => setAddForm({ ...addForm, mobile: e.target.value })} placeholder="+91 …" />
              </div>

              <fieldset className="space-y-1.5">
                <legend className="text-sm font-medium">Role</legend>
                <div className="flex flex-col gap-2">
                  {roles.map((r: any) => {
                    const id = String(r.id || r.Id);
                    const on = addForm.user_role_id === id;
                    return (
                      <label key={id} className={cn("flex cursor-pointer items-start gap-2.5 rounded-lg border px-3 py-2.5", on ? "border-primary bg-accent/40" : "border-input bg-card hover:bg-muted/50")}>
                        <input type="radio" name="invite-role" checked={on} onChange={() => setAddForm({ ...addForm, user_role_id: id, manager_id: "" })} className="mt-1 accent-primary" />
                        <span>
                          <span className="block text-[13px] font-semibold text-foreground">{r.name || r.RoleName}</span>
                          <span className="block text-xs text-muted-foreground">{ROLE_BLURB[String(r.code || r.RoleCode).toUpperCase()] || "Custom role."}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </fieldset>

              <div className="space-y-1.5">
                <Label>{isSalesExecRole(addForm.user_role_id) ? "Reports to (Sales head)" : "Reports to (optional)"}</Label>
                <SearchSelect
                  value={addForm.manager_id}
                  selectedLabel={addManagerLabel}
                  endpoint="/api/User/List"
                  extraParams={isSalesExecRole(addForm.user_role_id) && salesHeadRoleId ? { role_id: salesHeadRoleId } : {}}
                  placeholder="Search users"
                  allowEmpty={!isSalesExecRole(addForm.user_role_id)}
                  emptyLabel="Nobody (top level)"
                  getLabel={managerName}
                  filterRow={(row) => row.status !== false}
                  onChange={(id, row) => {
                    setAddManagerLabel(row ? managerName(row) : "");
                    setAddForm({ ...addForm, manager_id: id });
                  }}
                />
                {isSalesExecRole(addForm.user_role_id) && <p className="text-[11.5px] text-muted-foreground">Shown because a sales executive always belongs to a team.</p>}
              </div>

              <label className="flex cursor-pointer items-start gap-2.5 rounded-lg bg-muted/60 px-3 py-2.5 text-[12.5px] text-muted-foreground">
                <input type="checkbox" checked={addForm.send_welcome_email} onChange={(e) => setAddForm({ ...addForm, send_welcome_email: e.target.checked })} className="mt-0.5 size-4 accent-primary" />
                <span>
                  <span className="flex items-center gap-1.5 font-medium text-foreground">
                    <Mail className="size-3.5 text-primary" />
                    Email the invite
                  </span>
                  {addForm.first_name || "The person"} chooses their own password from the link. It works for 72 hours and once. You also get the link, to send it yourself if email is slow.
                </span>
              </label>
            </div>
            <SheetFooter className="flex-row justify-end gap-2 border-t border-border px-5 py-3">
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionLoading}>
                {actionLoading ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
                Send invite
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      {/* Dialog: Edit User */}
      <Dialog open={!!editUser} onOpenChange={(open) => !open && setEditUser(null)}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit User Profile</DialogTitle>
            <DialogDescription>
              Update basic account information and contact numbers
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4 py-2 text-xs sm:text-sm">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">First Name</Label>
                <Input
                  value={editForm.first_name}
                  onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">Last Name</Label>
                <Input
                  value={editForm.last_name}
                  onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm">Email Address</Label>
              <BusinessEmailInput domain={businessDomain} value={editForm.email} onChange={(email) => setEditForm({ ...editForm, email })} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">Username</Label>
                <Input
                  value={editForm.user_name}
                  onChange={(e) => setEditForm({ ...editForm, user_name: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">Mobile</Label>
                <Input
                  value={editForm.mobile}
                  onChange={(e) => setEditForm({ ...editForm, mobile: e.target.value })}
                />
              </div>
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setEditUser(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionLoading}>
                {actionLoading ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: Change Role & Reporting Manager */}
      <Dialog open={!!changeRoleUser} onOpenChange={(open) => !open && setChangeRoleUser(null)}>
        <DialogContent className="sm:max-w-lg md:max-w-xl">
          <DialogHeader>
            <DialogTitle>Update Security Role & Hierarchy</DialogTitle>
            <DialogDescription>
              Assign role privileges and reporting manager for {changeRoleUser?.first_name || changeRoleUser?.username}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleRoleSubmit} className="space-y-4 py-2 text-xs sm:text-sm">
            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm">Assigned Security Role *</Label>
              <Select value={newRoleId} onValueChange={setNewRoleId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select new role" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((r: any) => (
                    <SelectItem key={r.id || r.Id} value={String(r.id || r.Id)}>
                      {r.name || r.RoleName} ({r.code || r.RoleCode})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm">
                {isSalesExecRole(newRoleId) ? "Reports To (Sales Head) *" : "Reporting Manager"}
              </Label>
              <SearchSelect
                value={newManagerId === "none" ? "" : newManagerId}
                selectedLabel={roleManagerLabel}
                endpoint="/api/User/List"
                extraParams={isSalesExecRole(newRoleId) && salesHeadRoleId ? { role_id: salesHeadRoleId } : {}}
                placeholder="Search users"
                allowEmpty={!isSalesExecRole(newRoleId)}
                emptyLabel="None (Top Level / Direct)"
                getLabel={managerName}
                filterRow={(row) => String(row.id) !== String(changeRoleUser?.id) && row.status !== false}
                onChange={(id, row) => {
                  setRoleManagerLabel(row ? managerName(row) : "");
                  setNewManagerId(id || "none");
                }}
              />
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setChangeRoleUser(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionLoading || !newRoleId}>
                {actionLoading ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
                Save Role & Manager
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: one-time invite / reset link */}
      <Dialog open={!!sharedLink} onOpenChange={(open) => !open && setSharedLink(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{sharedLink?.kind === "invite" ? "Invite link" : "Password reset link"}</DialogTitle>
            <DialogDescription>
              {sharedLink?.emailed ? "We emailed this link to " : "Send this link to "}
              <strong className="text-foreground">{sharedLink?.email}</strong>
              {sharedLink?.emailed
                ? ". You can also share it yourself, privately."
                : " yourself, privately. It was not emailed: no company mailbox is connected under Emails → Email Connection (or sending failed)."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="flex gap-2">
              <Input readOnly value={sharedLink?.url || ""} className="font-mono text-xs" onFocus={(e) => e.currentTarget.select()} />
              <Button type="button" onClick={copyLink} className="shrink-0">
                <Copy className="size-4 mr-1.5" />
                Copy
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              Works once and expires {sharedLink?.expires_at ? new Date(sharedLink.expires_at).toLocaleString() : ""}. It is shown only
              now: the CRM keeps just a scrambled copy, so close this and use{" "}
              {sharedLink?.kind === "invite" ? "Resend Invite" : "Send Password Reset Link"} if you need it again. Anyone with the
              link can set this account's password, so don't post it in a group chat.
            </p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setSharedLink(null)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Delete Confirmation */}
      <Dialog open={!!deleteUser} onOpenChange={(open) => !open && setDeleteUser(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-danger">Delete User Account</DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete account{" "}
              <strong className="text-foreground">{deleteUser?.email}</strong>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-3">
            <Button type="button" variant="outline" onClick={() => setDeleteUser(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={actionLoading}
            >
              {actionLoading ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
              Confirm Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
